import { useEffect, useState } from 'react';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { PhoneInput, emptyPhoneValue, getDialCode } from '../components/PhoneInput';
import { PrimaryButton } from '../components/PrimaryButton';
import {
  getAppSupport,
  submitContactMessage,
  type AppSupport,
} from '../../lib/site-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

export default function ContactUsPage() {
  const [support, setSupport] = useState<AppSupport | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: emptyPhoneValue('GB'),
    message: '',
  });
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    mobileNumber: false,
    message: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    getAppSupport()
      .then((res) => setSupport(res.info))
      .catch(() => setSupport(null));
  }, []);

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async () => {
    const trimmedMobile = form.phone.mobileNumber.trim();
    const mobileInvalid =
      trimmedMobile.length > 0 && !/^\d{6,15}$/.test(trimmedMobile);
    const newErrors = {
      name: !form.name.trim(),
      email: !validateEmail(form.email),
      mobileNumber: mobileInvalid,
      message: form.message.trim().length < 10,
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    setSubmitting(true);
    try {
      await submitContactMessage({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        message: form.message.trim(),
        // Send mobile only when filled — backend treats it as optional
        // alongside email. dial_code is required when mobile is present.
        ...(trimmedMobile
          ? {
              mobile_number: trimmedMobile,
              dial_code: getDialCode(form.phone.country),
            }
          : {}),
      });
      setSubmitted(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send your message.'));
    } finally {
      setSubmitting(false);
    }
  };

  const phone = support?.mobile_number
    ? `${support.mobile_dial_code ?? ''} ${support.mobile_number}`.trim()
    : null;
  const whatsapp = support?.whatsapp_mobile_number
    ? `${support.whatsapp_dial_code ?? ''} ${support.whatsapp_mobile_number}`.trim()
    : null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />
      <main className="flex-1 py-12 px-4">
        <div className="max-w-[820px] mx-auto">
          <div className="text-center mb-10">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[34px] leading-[48px] mb-2">
              Contact us
            </h1>
            <p className="text-[#908e8e] text-[14px]">
              We'd love to hear from you — we typically respond within one business day.
            </p>
          </div>

          {(support?.email || phone || whatsapp) && (
            // Centered, form-width contact block so heading → contact →
            // form read as one cohesive column. flex+justify-center keeps
            // a single card (the common case: only a support email) from
            // being stranded in a left grid column on desktop (B4).
            <div className="flex flex-wrap justify-center gap-4 mb-10 max-w-[520px] mx-auto">
              {support?.email && (
                <div className="w-full md:w-[240px] bg-[#fafaf7] border border-[#ede8e9] rounded-xl p-4 text-center">
                  <span className="material-icons text-[#8e2a2f] text-[28px]">email</span>
                  <p className="text-[#3a3335] font-bold text-[14px] mt-2">Email</p>
                  <a
                    href={`mailto:${support.email}`}
                    className="text-[#8e2a2f] text-[13px] underline break-all"
                  >
                    {support.email}
                  </a>
                </div>
              )}
              {phone && (
                <div className="w-full md:w-[240px] bg-[#fafaf7] border border-[#ede8e9] rounded-xl p-4 text-center">
                  <span className="material-icons text-[#8e2a2f] text-[28px]">phone</span>
                  <p className="text-[#3a3335] font-bold text-[14px] mt-2">Phone</p>
                  <a href={`tel:${phone.replace(/\s+/g, '')}`} className="text-[#8e2a2f] text-[13px]">
                    {phone}
                  </a>
                </div>
              )}
              {whatsapp && (
                <div className="w-full md:w-[240px] bg-[#fafaf7] border border-[#ede8e9] rounded-xl p-4 text-center">
                  <span className="material-icons text-[#8e2a2f] text-[28px]">chat</span>
                  <p className="text-[#3a3335] font-bold text-[14px] mt-2">WhatsApp</p>
                  <a
                    href={`https://wa.me/${whatsapp.replace(/[^\d]/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#8e2a2f] text-[13px]"
                  >
                    {whatsapp}
                  </a>
                </div>
              )}
            </div>
          )}

          {submitted ? (
            <div className="text-center bg-[#fafaf7] border border-[#ede8e9] rounded-xl p-8">
              <span className="material-icons text-[#065f46] text-[40px]">check_circle</span>
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[22px] mt-2">
                Message sent
              </h2>
              <p className="text-[#908e8e] text-[14px] mt-1">
                Thanks for reaching out — we'll be in touch shortly.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-5 max-w-[520px] mx-auto">
              <FloatingLabelInput
                label="Your name"
                value={form.name}
                onChange={(v) => {
                  setForm({ ...form, name: v });
                  setErrors({ ...errors, name: false });
                }}
                error={errors.name}
                errorMessage="Name is required"
              />
              <FloatingLabelInput
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => {
                  setForm({ ...form, email: v });
                  setErrors({ ...errors, email: false });
                }}
                error={errors.email}
                errorMessage="Please enter a valid email address"
              />
              <PhoneInput
                label="Mobile number (optional)"
                value={form.phone}
                onChange={(next) => {
                  setForm({ ...form, phone: next });
                  setErrors({ ...errors, mobileNumber: false });
                }}
                error={errors.mobileNumber ? 'Enter 6–15 digits' : null}
                placeholder="7700900000"
              />
              <div>
                <label className="block text-[12px] text-[#908e8e] font-bold mb-2">
                  Message
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => {
                    setForm({ ...form, message: e.target.value });
                    setErrors({ ...errors, message: false });
                  }}
                  rows={6}
                  className={`w-full rounded-[8px] border ${errors.message ? 'border-red-500' : 'border-[#eee]'} bg-white p-4 font-['DM_Sans',sans-serif] text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f] transition-colors`}
                  placeholder="Tell us how we can help…"
                />
                {errors.message && (
                  <p className="mt-1 text-red-500 text-[12px]">
                    Please write at least 10 characters.
                  </p>
                )}
              </div>

              <PrimaryButton
                onClick={submitting ? undefined : handleSubmit}
                className={`w-full py-4 ${submitting ? 'opacity-60 pointer-events-none' : ''}`}
              >
                {submitting ? 'Sending…' : 'Send message'}
              </PrimaryButton>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
